import React, { useEffect, useState, useCallback } from "react";
import AppShell from "@/components/layout/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { RefreshCw, Cloud, CheckCircle2, ShoppingBag, Loader2, Package } from "lucide-react";
import { formatPrice } from "@/lib/cart";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUS_BADGE = {
  COMPLETED: "bg-[hsl(142,42%,34%,0.15)] text-[hsl(142,42%,26%)] border border-[hsl(142,42%,34%,0.25)]",
  APPROVED: "bg-[hsl(142,42%,34%,0.15)] text-[hsl(142,42%,26%)] border border-[hsl(142,42%,34%,0.25)]",
  PAYMENT_FAILED: "bg-[hsl(6,72%,46%,0.12)] text-[hsl(6,72%,36%)] border border-[hsl(6,72%,46%,0.22)]",
  FAILED: "bg-[hsl(6,72%,46%,0.12)] text-[hsl(6,72%,36%)] border border-[hsl(6,72%,46%,0.22)]",
  OPEN: "bg-[hsl(38,92%,45%,0.15)] text-[hsl(38,92%,30%)] border border-[hsl(38,92%,45%,0.25)]",
  PAYMENT_PENDING: "bg-[hsl(38,92%,45%,0.15)] text-[hsl(38,92%,30%)] border border-[hsl(38,92%,45%,0.25)]",
};

export default function AdminShop() {
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [loadingSync, setLoadingSync] = useState(true);

  const loadSyncStatus = useCallback(() => {
    setLoadingSync(true);
    api.get("/shop/admin/catalog/status")
      .then(({ data }) => setSyncStatus(data))
      .catch(() => {})
      .finally(() => setLoadingSync(false));
  }, []);

  const loadOrders = useCallback(() => {
    setLoadingOrders(true);
    api.get("/shop/admin/orders?limit=25")
      .then(({ data }) => { setOrders(data.orders || []); setOrdersTotal(data.total || 0); })
      .catch(() => {})
      .finally(() => setLoadingOrders(false));
  }, []);

  useEffect(() => { loadSyncStatus(); loadOrders(); }, [loadSyncStatus, loadOrders]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/shop/admin/catalog/sync");
      toast.success(data.message || "Catalog synced");
      loadSyncStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <AppShell variant="admin" title="Shop Management">
      <div className="max-w-5xl space-y-6" data-testid="admin-shop-page">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-[hsl(var(--fg))]" data-testid="admin-shop-title">
            Shop Management
          </h1>
        </div>

        {/* Catalog Sync Card */}
        <Card className="border border-[hsl(var(--border))]">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Cloud className="h-4 w-4" /> Square Catalog Sync
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-1">
                {loadingSync ? (
                  <Skeleton className="h-4 w-64" />
                ) : syncStatus ? (
                  <div className="space-y-1">
                    <p className="text-sm">
                      <span className="text-[hsl(var(--muted-fg))]">Products:</span>{" "}
                      <span className="font-medium">{syncStatus.product_count}</span>
                    </p>
                    {syncStatus.last_synced_at && (
                      <p className="text-xs text-[hsl(var(--muted-fg))]">
                        Last synced: {new Date(syncStatus.last_synced_at).toLocaleString("en-AU")}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-[hsl(var(--muted-fg))]">No catalog data yet. Run a sync to import products from Square.</p>
                )}
              </div>
              <Button
                onClick={handleSync}
                disabled={syncing}
                className="gap-2 bg-[hsl(var(--primary))] text-[hsl(var(--primary-fg))] min-h-[44px]"
                data-testid="admin-sync-catalog-button"
              >
                {syncing ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Syncing...</>
                ) : (
                  <><RefreshCw className="h-4 w-4" /> Sync Catalog</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Orders Table */}
        <Card className="border border-[hsl(var(--border))]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <ShoppingBag className="h-4 w-4" /> Recent Orders ({ordersTotal})
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={loadOrders} data-testid="admin-refresh-orders">
                <RefreshCw className="h-3.5 w-3.5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loadingOrders ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-8">
                <Package className="h-10 w-10 mx-auto text-[hsl(var(--muted-fg))] mb-3" />
                <p className="text-sm text-[hsl(var(--muted-fg))]">No orders yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[hsl(var(--secondary))]">
                      <TableHead>Order ID</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.map((order) => (
                      <TableRow key={order.id} data-testid={`admin-order-row-${order.id}`}>
                        <TableCell>
                          <code className="text-xs font-mono">{(order.id || "").slice(0, 8)}...</code>
                        </TableCell>
                        <TableCell className="text-sm max-w-[200px] truncate">
                          {(order.line_items || []).map((li) => li.name).join(", ") || "—"}
                        </TableCell>
                        <TableCell className="text-sm">
                          {order.guest_email || (order.user_id ? "Member" : "Guest")}
                        </TableCell>
                        <TableCell className="font-medium">
                          {order.total_money
                            ? formatPrice(order.total_money.amount, order.total_money.currency)
                            : "—"}
                        </TableCell>
                        <TableCell>
                          <Badge className={STATUS_BADGE[order.status] || STATUS_BADGE.OPEN}>
                            {order.status || "Unknown"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-[hsl(var(--muted-fg))]">
                          {order.created_at
                            ? new Date(order.created_at).toLocaleDateString("en-AU", {
                                day: "numeric", month: "short",
                              })
                            : ""}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
